import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API as API_BASE } from '../../lib/api';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatDateInZone, resolveDisplayZone } from '../../lib/timezone';
import { KeyRound, ShieldCheck, MonitorSmartphone, Loader2, Copy, Check, Download } from 'lucide-react';
import SettingsLayout from './SettingsLayout';
import MfaEnrollment from '../../components/auth/MfaEnrollment';
import MfaCodeInput from '../../components/auth/MfaCodeInput';
// #603: a FastAPI 422 makes `detail` an ARRAY; assigning it into state and
// rendering it unmounted this page. errorText() is the single reader.
import { errorText } from '../../lib/apiErrors';

// MfaEnrollment posts relative paths ('/auth/mfa/setup'), so it MUST get an
// instance scoped to the API base — bare axios would hit the frontend origin
// and get index.html back (issue #557, same latent #523 defect as Login).
const mfaApi = axios.create({ baseURL: API_BASE });

/**
 * Settings → Security (plan §12.3).
 *
 * Four blocks: Password, Two-step verification, Sessions, Trusted devices
 * (issue #564).
 * Every factor-changing action asks for a current TOTP code inline (2.4.1).
 *
 * The MFA state is read from `user.mfa_enabled` / `user.mfa_enrolled_at` (both
 * are on the serialized user doc from /api/auth/me and login). After a factor
 * change we re-fetch /auth/me and push the fresh user into context.
 */
export default function SettingsSecurityPage() {
    const { user, getAuthHeader, changePassword, revokeAllSessions, setGoogleUser } = useAuth();

    // MFA state — initialized from the session user, updated after factor
    // changes so the page reflects reality without a full reload.
    const [mfaEnabled, setMfaEnabled] = useState(Boolean(user?.mfa_enabled));
    const [mfaEnrolledAt, setMfaEnrolledAt] = useState(user?.mfa_enrolled_at || null);

    // Password block
    const [passwordOpen, setPasswordOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState(null);

    // Two-step verification block
    const [enrolling, setEnrolling] = useState(false); // show the MfaEnrollment flow
    const [regenerating, setRegenerating] = useState(false); // show inline TOTP for regenerate
    const [disabling, setDisabling] = useState(false); // show inline TOTP for disable
    const [factorCode, setFactorCode] = useState('');
    const [factorError, setFactorError] = useState(null);
    const [factorBusy, setFactorBusy] = useState(false);

    // Recovery codes shown after enable / regenerate (with a save gate)
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);

    // Sessions block
    const [revoking, setRevoking] = useState(false);

    // Trusted devices block (issue #564)
    const [trustedDevices, setTrustedDevices] = useState([]);
    const [trustedLoading, setTrustedLoading] = useState(true);
    const [revokingDeviceId, setRevokingDeviceId] = useState(null);
    const [revokingAllDevices, setRevokingAllDevices] = useState(false);

    const isAdmin = Boolean(user?.is_admin || user?.role === 'admin');
    // Enforcement is on by default (MFA_ENFORCE_ADMIN=true); admins cannot turn
    // their factor off while it is enforced (plan §11 / D3).
    const mfaEnforcedForAdmin = isAdmin;

    // Issue #564: render an ISO timestamp defensively — a malformed value must
    // never blank the whole block.
    const formatDeviceDate = (iso) => {
        if (!iso) return '—';
        try {
            return format(new Date(iso), 'MMM d, yyyy h:mm a');
        } catch {
            return String(iso);
        }
    };

    const refreshUser = async () => {
        try {
            const res = await axios.get(`${API_BASE}/auth/me`, { headers: getAuthHeader() });
            setGoogleUser(res.data);
            setMfaEnabled(Boolean(res.data?.mfa_enabled));
            setMfaEnrolledAt(res.data?.mfa_enrolled_at || null);
        } catch {
            // Non-fatal: state already updated locally below.
        }
    };

    // ── Trusted devices (issue #564) ──────────────────────────────────────
    // The endpoints live OUTSIDE authRefresh.js SKIP_PATHS, so a 401 here gets
    // the global silent-refresh-and-retry for free. Responses never contain
    // token material — the server stores only SHA-256 digests.
    const loadTrustedDevices = async () => {
        setTrustedLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/auth/trusted-devices`, { headers: getAuthHeader() });
            setTrustedDevices(res.data?.devices || []);
        } catch {
            // Non-fatal: the block renders with an error hint via empty list.
            setTrustedDevices([]);
        } finally {
            setTrustedLoading(false);
        }
    };

    useEffect(() => {
        loadTrustedDevices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const revokeTrustedDevice = async (deviceId) => {
        setRevokingDeviceId(deviceId);
        try {
            await axios.delete(`${API_BASE}/auth/trusted-devices/${deviceId}`, { headers: getAuthHeader() });
            toast.success('Trusted device revoked. That device will need MFA at next sign-in.');
            await loadTrustedDevices();
        } catch (err) {
            toast.error(errorText(err, 'Could not revoke the device. Try again.'));
        } finally {
            setRevokingDeviceId(null);
        }
    };

    const revokeAllTrustedDevices = async () => {
        if (!window.confirm('Revoke all trusted devices? Every remembered device will require MFA at next sign-in.')) {
            return;
        }
        setRevokingAllDevices(true);
        try {
            await axios.post(`${API_BASE}/auth/trusted-devices/revoke-all`, {}, { headers: getAuthHeader() });
            toast.success('All trusted devices revoked.');
            await loadTrustedDevices();
        } catch (err) {
            toast.error(errorText(err, 'Could not revoke devices. Try again.'));
        } finally {
            setRevokingAllDevices(false);
        }
    };

    // ── Password ───────────────────────────────────────────────────────────
    const submitPassword = async () => {
        setPasswordError(null);
        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError('Fill in all three fields.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('New password and confirmation do not match.');
            return;
        }
        setChangingPassword(true);
        try {
            await changePassword(currentPassword, newPassword);
            toast.success('Password changed. Other devices were signed out.');
            setPasswordOpen(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) {
                // Deliberately ignores `detail` — a fixed sentence is correct here.
                setPasswordError('Current password is incorrect');
            } else if (status === 400) {
                setPasswordError(errorText(err, 'Password must be at least 8 characters'));
            } else {
                setPasswordError(errorText(err, 'Could not change your password. Please try again.'));
            }
        } finally {
            setChangingPassword(false);
        }
    };

    // ── Two-step verification ──────────────────────────────────────────────
    // `result` is the whole /auth/mfa/enable response. On the LOGIN path it
    // carries a session (#594 — enrollment signs the user in there, removing the
    // second code prompt), but this page enrols with a full session already in
    // hand, so the backend deliberately keeps today's shape here and there is
    // nothing to adopt. Take the argument anyway rather than relying on its
    // absence.
    const handleEnrollmentComplete = async (result) => {
        toast.success('Two-step verification is on.');
        if (result?.recovery_codes?.length) setRecoveryCodes(result.recovery_codes);
        await refreshUser();
        setEnrolling(false);
    };

    const copyRecoveryCodes = async () => {
        try {
            await navigator.clipboard.writeText(recoveryCodes.join('\n'));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Ignore clipboard failure — the download button is the fallback.
        }
    };

    const downloadRecoveryCodes = () => {
        const blob = new Blob(
            [`NurtureCRM recovery codes\nEach code works once.\n\n${recoveryCodes.join('\n')}\n`],
            { type: 'text/plain' }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'nurturecrm-recovery-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const regenerateCodes = async (submitted) => {
        const code = (submitted ?? factorCode).trim();
        if (!code) return;
        setFactorBusy(true);
        setFactorError(null);
        try {
            const res = await axios.post(
                `${API_BASE}/auth/mfa/recovery/regenerate`,
                { code },
                { headers: getAuthHeader() }
            );
            setRecoveryCodes(res.data.recovery_codes || []);
            setSaved(false);
            setRegenerating(false);
            setFactorCode('');
        } catch (err) {
            setFactorError(errorText(err, "That code didn't work. Try again."));
            setFactorCode('');
        } finally {
            setFactorBusy(false);
        }
    };

    const disableMfa = async (submitted) => {
        const code = (submitted ?? factorCode).trim();
        if (!code) return;
        setFactorBusy(true);
        setFactorError(null);
        try {
            await axios.post(
                `${API_BASE}/auth/mfa/disable`,
                { code },
                { headers: getAuthHeader() }
            );
            toast.success('Two-step verification is off.');
            setMfaEnabled(false);
            setMfaEnrolledAt(null);
            setDisabling(false);
            setFactorCode('');
        } catch (err) {
            const status = err?.response?.status;
            if (status === 403) {
                setFactorError(errorText(err, 'Required for administrators on this workspace.'));
            } else {
                setFactorError(errorText(err, "That code didn't work. Try again."));
            }
            setFactorCode('');
        } finally {
            setFactorBusy(false);
        }
    };

    // ── Sessions ───────────────────────────────────────────────────────────
    const signOutOtherDevices = async () => {
        setRevoking(true);
        try {
            await revokeAllSessions();
            toast.success('Signed out of all other devices.');
        } catch {
            toast.error('Could not sign out other devices. Please try again.');
        } finally {
            setRevoking(false);
        }
    };

    const enrolledDate = mfaEnrolledAt
        // #541: absolute timestamps route through the shared tz-aware formatter
        // (active viewer zone). `user` comes from useAuth above.
        ? `enrolled ${formatDateInZone(mfaEnrolledAt, { zone: resolveDisplayZone({ user }) })}`
        : null;

    return (
        <SettingsLayout title="Security" description="Manage your account security and sessions">
            <div className="max-w-lg space-y-5">
                {/* Account email */}
                <div>
                    <p className="text-sm font-medium text-stone-900 mb-1">Account Email</p>
                    <p className="text-sm text-stone-500">{user?.email}</p>
                </div>

                {/* ── Password ─────────────────────────────────────────────── */}
                <div className="border border-stone-200 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-2 flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-stone-500" /> Password
                    </h3>
                    <p className="text-sm text-stone-500 mb-4">
                        Change your password. You'll stay signed in here; other devices will be signed out.
                    </p>

                    {!passwordOpen ? (
                        <button
                            type="button"
                            onClick={() => setPasswordOpen(true)}
                            data-testid="security-change-password-btn"
                            className="px-4 py-2 text-sm font-medium text-stone-700 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                        >
                            Change password
                        </button>
                    ) : (
                        <div className="space-y-3" data-testid="security-password-form">
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Current password</label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    autoComplete="current-password"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">New password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoComplete="new-password"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Confirm new password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    autoComplete="new-password"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                />
                            </div>

                            {passwordError && (
                                <p data-testid="security-password-error" className="text-sm text-red-600">{passwordError}</p>
                            )}

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={submitPassword}
                                    disabled={changingPassword}
                                    data-testid="security-password-submit"
                                    className="px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50"
                                >
                                    {changingPassword ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
                                    {changingPassword ? 'Changing…' : 'Update password'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setPasswordOpen(false); setPasswordError(null); }}
                                    className="px-4 py-2 text-sm font-medium text-stone-500 hover:text-stone-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Two-step verification ────────────────────────────────── */}
                <div className="border border-stone-200 rounded-lg p-5">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-stone-900 flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-stone-500" /> Two-step verification
                        </h3>
                        {mfaEnabled && (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> On
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-stone-500 mt-1">
                        Authenticator app on this account.{mfaEnabled && enrolledDate ? ` (${enrolledDate})` : ''}
                    </p>

                    {!mfaEnabled && !enrolling && (
                        <button
                            type="button"
                            onClick={() => setEnrolling(true)}
                            data-testid="security-enable-mfa-btn"
                            className="mt-4 px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800"
                        >
                            Turn on
                        </button>
                    )}

                    {!mfaEnabled && enrolling && (
                        <div className="mt-4">
                            <MfaEnrollment
                                api={mfaApi}
                                headers={getAuthHeader()}
                                onComplete={handleEnrollmentComplete}
                            />
                            <button
                                type="button"
                                onClick={() => setEnrolling(false)}
                                className="mt-3 text-sm text-stone-500 hover:text-stone-700"
                            >
                                Cancel
                            </button>
                        </div>
                    )}

                    {mfaEnabled && (
                        <div className="mt-4 space-y-3">
                            {/* Recovery codes shown after enable / regenerate */}
                            {recoveryCodes.length > 0 && (
                                <div className="space-y-3" data-testid="security-recovery-codes-screen">
                                    <div className="grid grid-cols-2 gap-2 max-w-xs" data-testid="security-recovery-codes">
                                        {recoveryCodes.map((c) => (
                                            <code key={c} className="text-sm font-mono text-stone-800 bg-stone-50 border border-stone-200 rounded px-2 py-1 text-center">
                                                {c}
                                            </code>
                                        ))}
                                    </div>
                                    <div className="flex justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={copyRecoveryCodes}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700"
                                        >
                                            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                                            {copied ? 'Copied' : 'Copy all'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={downloadRecoveryCodes}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700"
                                        >
                                            <Download className="w-4 h-4" /> Download .txt
                                        </button>
                                    </div>
                                    <label className="flex items-center justify-center gap-2 cursor-pointer" data-testid="security-saved-gate">
                                        <input
                                            type="checkbox"
                                            checked={saved}
                                            onChange={(e) => setSaved(e.target.checked)}
                                            className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20"
                                        />
                                        <span className="text-sm text-stone-700">I've saved these somewhere safe</span>
                                    </label>
                                    <div className="flex justify-center">
                                        <button
                                            type="button"
                                            disabled={!saved}
                                            onClick={() => setRecoveryCodes([])}
                                            data-testid="security-recovery-continue-btn"
                                            className="px-5 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Continue
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Regenerate codes — inline TOTP */}
                            {regenerating && (
                                <div className="space-y-3" data-testid="security-regenerate-flow">
                                    <p className="text-sm text-stone-500">Enter a current code from your authenticator to regenerate your recovery codes.</p>
                                    <MfaCodeInput
                                        value={factorCode}
                                        onChange={setFactorCode}
                                        onSubmit={regenerateCodes}
                                        error={factorError}
                                        disabled={factorBusy}
                                    />
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => regenerateCodes()}
                                            disabled={factorBusy || !factorCode.trim()}
                                            className="px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50"
                                        >
                                            {factorBusy ? 'Regenerating…' : 'Regenerate codes'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setRegenerating(false); setFactorError(null); setFactorCode(''); }}
                                            className="px-4 py-2 text-sm font-medium text-stone-500 hover:text-stone-700"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Turn off — inline TOTP */}
                            {disabling && (
                                <div className="space-y-3" data-testid="security-disable-flow">
                                    <p className="text-sm text-stone-500">Enter a current code from your authenticator to turn off two-step verification.</p>
                                    <MfaCodeInput
                                        value={factorCode}
                                        onChange={setFactorCode}
                                        onSubmit={disableMfa}
                                        error={factorError}
                                        disabled={factorBusy}
                                    />
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => disableMfa()}
                                            disabled={factorBusy || !factorCode.trim()}
                                            className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                                        >
                                            {factorBusy ? 'Turning off…' : 'Turn off'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setDisabling(false); setFactorError(null); setFactorCode(''); }}
                                            className="px-4 py-2 text-sm font-medium text-stone-500 hover:text-stone-700"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!regenerating && !disabling && recoveryCodes.length === 0 && (
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setRegenerating(true)}
                                        data-testid="security-regenerate-codes-btn"
                                        className="px-4 py-2 text-sm font-medium text-stone-700 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                                    >
                                        Regenerate codes
                                    </button>
                                    {mfaEnforcedForAdmin ? (
                                        <p className="text-sm text-stone-500 self-center" data-testid="security-admin-enforced-note">
                                            Required for administrators on this workspace.
                                        </p>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setDisabling(true)}
                                            data-testid="security-disable-mfa-btn"
                                            className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                                        >
                                            Turn off
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Sessions ─────────────────────────────────────────────── */}
                <div className="border border-stone-200 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-stone-900 mb-2 flex items-center gap-2">
                        <MonitorSmartphone className="w-4 h-4 text-stone-500" /> Sessions
                    </h3>
                    <p className="text-sm text-stone-500 mb-4">
                        You're signed in on this device. Signing out everywhere ends every other session, including the mobile app and the browser extension.
                    </p>
                    <button
                        type="button"
                        onClick={signOutOtherDevices}
                        disabled={revoking}
                        data-testid="security-revoke-all-btn"
                        className="px-4 py-2 text-sm font-medium text-stone-700 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors disabled:opacity-50"
                    >
                        {revoking ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
                        {revoking ? 'Signing out…' : 'Sign out of all other devices'}
                    </button>
                </div>

                {/* ── Trusted devices (issue #564) ─────────────────────────── */}
                <div className="border border-stone-200 rounded-lg p-5" data-testid="trusted-devices-card">
                    <h3 className="text-sm font-semibold text-stone-900 mb-2 flex items-center gap-2">
                        <MonitorSmartphone className="w-4 h-4 text-stone-500" /> Trusted devices
                    </h3>
                    <p className="text-sm text-stone-500 mb-4">
                        Devices you chose to trust for 7 days at sign-in. A trusted device skips the two-step prompt on
                        password sign-in. Revoking takes effect at the next sign-in.
                    </p>
                    {trustedLoading ? (
                        <p className="text-sm text-stone-500" data-testid="trusted-devices-loading">
                            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
                        </p>
                    ) : trustedDevices.length === 0 ? (
                        <p className="text-sm text-stone-500" data-testid="trusted-devices-empty">
                            No trusted devices. You can trust a device when signing in with two-step verification.
                        </p>
                    ) : (
                        <ul className="space-y-3" data-testid="trusted-devices-list">
                            {trustedDevices.map((device) => (
                                <li key={device.device_id} className="flex items-start justify-between gap-3" data-testid="trusted-device-row">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-stone-900 truncate">
                                            {device.label || 'Unnamed device'}
                                        </p>
                                        <p className="text-xs text-stone-500">
                                            Added {formatDeviceDate(device.created_at)}
                                            {' · '}Last used {formatDeviceDate(device.last_used_at)}
                                            {' · '}Expires {formatDeviceDate(device.expires_at)}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (window.confirm(`Revoke "${device.label || 'Unnamed device'}"? It will need two-step verification at next sign-in.`)) {
                                                revokeTrustedDevice(device.device_id);
                                            }
                                        }}
                                        disabled={revokingDeviceId === device.device_id}
                                        data-testid={`trusted-device-revoke-${device.device_id}`}
                                        className="shrink-0 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                                    >
                                        {revokingDeviceId === device.device_id ? 'Revoking…' : 'Revoke'}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {trustedDevices.length > 0 ? (
                        <button
                            type="button"
                            onClick={revokeAllTrustedDevices}
                            disabled={revokingAllDevices}
                            data-testid="trusted-devices-revoke-all-btn"
                            className="mt-4 px-4 py-2 text-sm font-medium text-stone-700 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors disabled:opacity-50"
                        >
                            {revokingAllDevices ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
                            {revokingAllDevices ? 'Revoking…' : 'Revoke all trusted devices'}
                        </button>
                    ) : null}
                </div>
            </div>
        </SettingsLayout>
    );
}
